import React, { useEffect } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, useLocation, Navigate, useNavigate } from 'react-router-dom';
import { CartProvider } from './context/CartContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SiteSettingsProvider, useSiteSettings } from './context/SiteSettingsContext';
import { RadioProvider } from './context/RadioContext';
import { Toaster } from './components/ui/toaster';
import FloatingNav from './components/FloatingNav';
import HomePage from './pages/HomePage';
import ShopPage from './pages/ShopPage';
import ProductDetailPage from './pages/ProductDetailPage';
import LegacyProductRedirectPage from './pages/LegacyProductRedirectPage';
import LegacyProductSlugRedirectPage from './pages/LegacyProductSlugRedirectPage';
import ContactPage from './pages/ContactPage';
import CheckoutPage from './pages/CheckoutPage';
import OrderConfirmationPage from './pages/OrderConfirmationPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ResearchLibraryPage from './pages/research/ResearchLibraryPage';
import ResearchArticlePage from './pages/research/ResearchArticlePage';
import FAQPage from './pages/FAQPage';
import AboutUsPage from './pages/AboutUsPage';
import AdminLayout from './pages/AdminLayout';
import AdminSettingsLayout from './pages/admin/AdminSettingsLayout';
import DevSettingsLayout from './pages/dev/DevSettingsLayout';
import MaintenancePage from './pages/MaintenancePage';

// Legal Pages
import PrivacyPolicyPage from './pages/legal/PrivacyPolicyPage';
import TermsConditionsPage from './pages/legal/TermsConditionsPage';
import AccessibilityPage from './pages/legal/AccessibilityPage';
import CompliancePage from './pages/legal/CompliancePage';
import ShippingReturnsPage from './pages/legal/ShippingReturnsPage';

// Location Pages (SEO)
import LocationPage from './pages/LocationPage';
import DevLocationPreview from './pages/dev/DevLocationPreview';

// User Portal
import UserPortal from './pages/UserPortal';

// New 123Bots Pages
import IndustryPage from './pages/IndustryPage';
import ScheduleDemoPage from './pages/ScheduleDemoPage';
import ResourcesPage from './pages/ResourcesPage';
import CategoryLandingPage from './pages/CategoryLandingPage';
import PublicBookingPage from './pages/PublicBookingPage';
import QuoteSigningPage from './pages/quotes/QuoteSigningPage';
import EventsIndexPage from './pages/EventsIndexPage';
import EventCategoryPage from './pages/EventCategoryPage';
import EventDetailPage from './pages/EventDetailPage';
import EventConfirmationPage from './pages/EventConfirmationPage';
import TicketViewPage from './pages/TicketViewPage';
import ActivitiesDirectoryPage from './pages/activities/ActivitiesDirectoryPage';
import ActivitiesByCompanyPage from './pages/activities/ActivitiesByCompanyPage';
import ToursChartersInvoiceView from './pages/activities/ToursChartersInvoiceView';
import ActivityListPage from './pages/activities/ActivityListPage';
import CompanySlugPage from './pages/activities/CompanySlugPage';
import ShopLandingPage from './pages/ShopLandingPage';
import ShopCategoryPage from './pages/ShopCategoryPage';
import ContactStJohnPage from './pages/ContactStJohnPage';
import VillaRentalsPage from './pages/VillaRentalsPage';
import AutoRentalsPage from './pages/island-info/AutoRentalsPage';
import BookYourTripPage from './pages/BookYourTripPage';
import TourCategoryLandingPage from './pages/activities/TourCategoryLandingPage';
import AllToursChartersPage from './pages/activities/AllToursChartersPage';
import WebcamsPage from './pages/island-info/WebcamsPage';
import FoodDrinkPage from './pages/island-info/FoodDrinkPage';
import HikingPage from './pages/island-info/HikingPage';
import BeachInfoPage from './pages/island-info/BeachInfoPage';
import WeatherPage from './pages/island-info/WeatherPage';
import TransportationPage from './pages/island-info/TransportationPage';
import CarBargePage from './pages/island-info/CarBargePage';
import PassengerFerryPage from './pages/island-info/PassengerFerryPage';
import WestinFerryPage from './pages/island-info/WestinFerryPage';
import IslandTaxiPage from './pages/island-info/IslandTaxiPage';
import PublicTransportationPage from './pages/island-info/PublicTransportationPage';
import IslandActivitiesMapPage from './pages/island-info/IslandActivitiesMapPage';
import ActivityDetailPage from './pages/activities/ActivityDetailPage';

// Age Verification
import AgeVerificationModal from './components/AgeVerificationModal';

// Chat Widget (Public Pages Only)
import ChatWidget from './components/ChatWidget';

import ChatPopout from './pages/admin/ChatPopout';

const A2G_ANALYTICS_SCRIPT_ID = 'ZwSg9rf6GA';
const A2G_ANALYTICS_SRC = 'https://a2ganalytics.com/js/script.js';

const A2GAnalyticsScript = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const isPublicRoute = !pathname.startsWith('/admin') && !pathname.startsWith('/dev');
    const existingScript = document.getElementById(A2G_ANALYTICS_SCRIPT_ID);

    if (!isPublicRoute) {
      return;
    }

    if (existingScript) {
      return;
    }

    const analyticsScript = document.createElement('script');
    analyticsScript.id = A2G_ANALYTICS_SCRIPT_ID;
    analyticsScript.src = A2G_ANALYTICS_SRC;
    analyticsScript.async = true;
    analyticsScript.defer = true;
    analyticsScript.setAttribute('data-host', 'https://a2ganalytics.com');
    analyticsScript.setAttribute('data-dnt', 'false');
    document.head.appendChild(analyticsScript);
  }, [pathname]);

  return null;
};

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

// Layout wrapper for public pages
const PublicLayout = ({ children }) => {
  const location = useLocation();
  const hideNav = location.pathname === '/login' || 
    location.pathname === '/register' ||
    location.pathname.startsWith('/admin') ||
    location.pathname.startsWith('/dev');

  return (
    <>
      <main className="min-h-screen bg-white">{children}</main>
      <ChatWidget />
    </>
  );
};

// Maintenance Mode Guard - wraps public routes
const MaintenanceGuard = ({ children }) => {
  const { maintenanceMode, loading } = useSiteSettings();
  const { isAuthenticated, user } = useAuth();
  
  // Show nothing while loading settings
  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-gold-400 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  // Check if user is admin (can bypass maintenance)
  const isAdmin = isAuthenticated && (user?.role === 'admin' || user?.role === 'super_admin');
  
  // Show maintenance page for non-admins when maintenance mode is enabled
  if (maintenanceMode && !isAdmin) {
    return <MaintenancePage />;
  }
  
  return children;
};

const ImpersonationBanner = () => {
  const { isImpersonating, impersonationAdmin, exitImpersonation } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  if (!isImpersonating) {
    return null;
  }

  const handleExitImpersonation = async () => {
    const result = await exitImpersonation();
    if (result.success) {
      if (!location.pathname.startsWith('/admin')) {
        navigate('/admin/user-management/customers');
      }
    }
  };

  return (
    <div className="sticky top-0 z-[60] bg-amber-500 text-black shadow" data-testid="impersonation-session-banner">
      <div className="max-w-[1600px] mx-auto px-4 py-2 flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-medium" data-testid="impersonation-session-label">
          Impersonating customer account
          {impersonationAdmin?.email ? ` • Admin: ${impersonationAdmin.email}` : ''}
        </p>
        <button
          type="button"
          onClick={handleExitImpersonation}
          className="px-3 py-1 text-sm rounded bg-black text-slate-900 hover:bg-gray-900 transition-colors"
          data-testid="exit-impersonation-button"
        >
          Exit Impersonation
        </button>
      </div>
    </div>
  );
};

// Inner app component that uses auth context
const AppContent = () => {
  return (
    <div className="App bg-white min-h-screen">
      <RadioProvider>
        <BrowserRouter>
          <A2GAnalyticsScript />
          <ImpersonationBanner />
          <ScrollToTop />
          <AgeVerificationModal />
          <Routes>
          {/* Dev Settings Routes (Super Admin Only) */}
          <Route path="/dev/settings/*" element={<DevSettingsLayout />} />
          
          {/* Dev Location Preview - Full page (no sidebar) */}
          <Route path="/dev/location-preview" element={<DevLocationPreview />} />
          
          {/* Admin Settings Routes */}
          <Route path="/admin/settings/*" element={<AdminSettingsLayout />} />
          
          {/* Chat Popout - Standalone window (no sidebar) */}
          <Route path="/admin/chat/:chatId" element={<ChatPopout />} />
          
          {/* Admin Routes */}
          <Route path="/admin/*" element={<AdminLayout />} />
          
          {/* Location Pages - Standalone (no PublicLayout wrapper) - also protected by maintenance */}
          <Route path="/locations/:slug" element={
            <MaintenanceGuard>
              <LocationPage />
            </MaintenanceGuard>
          } />
          
          {/* Public Routes - Protected by Maintenance Guard */}
            <Route path="/*" element={
              <MaintenanceGuard>
                <PublicLayout>
                  <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/shop" element={<ShopLandingPage />} />
                  <Route path="/shop/category/:slug" element={<ShopCategoryPage />} />
                  <Route path="/categories" element={<CategoryLandingPage />} />
                  <Route path="/shop/products" element={<ShopPage />} />
                  <Route path="/shop/:legacySlug" element={<LegacyProductSlugRedirectPage />} />
                  <Route path="/shop/:categorySlug/:productSlug" element={<ProductDetailPage />} />
                  <Route path="/product/:productId" element={<LegacyProductRedirectPage />} />
                  <Route path="/industries/:industrySlug" element={<IndustryPage />} />
                  <Route path="/schedule-a-demo" element={<ScheduleDemoPage />} />
                  <Route path="/island-info/st-john-live-webcams" element={<WebcamsPage />} />
                  <Route path="/island-info/food-drink" element={<FoodDrinkPage />} />
                  <Route path="/island-info/hiking-in-st-john" element={<HikingPage />} />
                  <Route path="/island-info/beach-info-st-john" element={<BeachInfoPage />} />
                  <Route path="/island-info/weather-in-st-john-vi" element={<WeatherPage />} />
                  <Route path="/island-info/transportation-st-john" element={<TransportationPage />} />
                  {/* backed by the real Tours & Charters directory (category slug "water-taxi")
                      so charter companies / images / booking links are admin-managed */}
                  <Route path="/water-taxi-st-john" element={<ActivityListPage mode="category" slugOverride="water-taxi" />} />
                  <Route path="/st-john-vi-car-barge-information" element={<CarBargePage />} />
                  <Route path="/st-john-vi-passenger-ferry-information" element={<PassengerFerryPage />} />
                  <Route path="/st-john-vi-westin-ferry-information" element={<WestinFerryPage />} />
                  <Route path="/island-taxi-st-john" element={<IslandTaxiPage />} />
                  <Route path="/public-transportation-st-john" element={<PublicTransportationPage />} />
                  <Route path="/island-activities-map" element={<IslandActivitiesMapPage />} />
                  {/* legacy "Jeep Rentals" tile URL - the real page is the Auto Rentals directory */}
                  <Route path="/auto-rentals-information" element={<Navigate to="/auto-rentals-st-john" replace />} />
                  <Route path="/resources" element={<ResourcesPage />} />
                  <Route path="/123-bots-resources" element={<Navigate to="/resources" replace />} />
                  <Route path="/events" element={<EventsIndexPage />} />
                  <Route path="/events/category/:catSlug" element={<EventCategoryPage />} />
                  <Route path="/events/confirmation" element={<EventConfirmationPage />} />
                  <Route path="/events/ticket/:code" element={<TicketViewPage />} />
                  <Route path="/events/:slug" element={<EventDetailPage />} />
                  <Route path="/all-tours-charters" element={<AllToursChartersPage />} />
                  <Route path="/activities" element={<ActivitiesDirectoryPage />} />
                  <Route path="/activities/companies" element={<ActivitiesByCompanyPage />} />
                  {/* legacy menu URL for the same page - "By Charter Company" links here */}
                  <Route path="/activities/charters-by-host-st-john-vi" element={<ActivitiesByCompanyPage />} />
                  <Route path="/activities/category/:slug" element={<ActivityListPage mode="category" />} />
                  <Route path="/activities/company/:slug" element={<ActivityListPage mode="company" />} />
                  <Route path="/activities/view/:slug" element={<ActivityDetailPage />} />
                  <Route path="/invoice/tours-charters/:invoiceId" element={<ToursChartersInvoiceView />} />
                    <Route path="/contact" element={<ContactPage />} />
                    {/* legacy contact URL, kept exactly */}
                    <Route path="/contacting-us-in-st-john" element={<ContactStJohnPage />} />
                    {/* footer ISLAND DIRECTORY -> Villa Rentals (top menu deliberately unchanged) */}
                    <Route path="/st-john-villa-rentals" element={<VillaRentalsPage />} />
                    {/* footer ISLAND DIRECTORY -> Auto Rental (legacy URL) */}
                    <Route path="/auto-rentals-st-john" element={<AutoRentalsPage />} />
                    {/* "Get Your FREE Villa Rental Quote" on the home page opens this */}
                    <Route path="/book-your-trip-to-st-john-vi" element={<BookYourTripPage />} />
                    <Route path="/booking/:bookingSlug" element={<PublicBookingPage />} />
                    <Route path="/book/:bookingSlug" element={<PublicBookingPage />} />
                    <Route path="/sign/:quoteId" element={<QuoteSigningPage />} />
                  <Route path="/checkout" element={<CheckoutPage />} />
                  <Route path="/order-confirmation" element={<OrderConfirmationPage />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/register" element={<RegisterPage />} />
                  <Route path="/verify-email" element={<VerifyEmailPage />} />
                  <Route path="/research" element={<ResearchLibraryPage />} />
                  <Route path="/research/:slug" element={<ResearchArticlePage />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                  <Route path="/terms-conditions" element={<TermsConditionsPage />} />
                  <Route path="/accessibility" element={<AccessibilityPage />} />
                  <Route path="/compliance" element={<CompliancePage />} />
                  <Route path="/shipping-returns" element={<ShippingReturnsPage />} />
                  <Route path="/faq" element={<FAQPage />} />
                  <Route path="/about" element={<AboutUsPage />} />
                  <Route path="/account" element={<UserPortal />} />
                  <Route path="/storage/*" element={<Navigate to="/" replace />} />
                  <Route path="/rv-repair/*" element={<Navigate to="/" replace />} />
                  <Route path="/employment/*" element={<Navigate to="/" replace />} />
                  <Route path="/job-application/*" element={<Navigate to="/" replace />} />
                    {/* Root-level charter company pages (e.g. /aqua-marine-dive-center)
                        preserve the legacy public URLs for SEO. Must stay directly
                        above the catch-all so all named routes take priority. */}
                    <Route path="/:slug" element={<CompanySlugPage />} />
                    {/* Legacy two-segment tour landing pages, e.g.
                        /tours-charters-st-john-virgin-islands/private-boat-charters.
                        Static routes above always win, so this only catches
                        category landing_path URLs. */}
                    <Route path="/:section/:landingSlug" element={<TourCategoryLandingPage />} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </PublicLayout>
              </MaintenanceGuard>
            } />
          </Routes>
        </BrowserRouter>
      </RadioProvider>
      <Toaster />
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <SiteSettingsProvider>
          <AppContent />
        </SiteSettingsProvider>
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
